<template>
  <div class="v2-affiliates v2-container v2-section">
    <header class="v2-affiliates__header">
      <div class="v2-affiliates__title-block">
        <p class="v2-overline">Membresía</p>
        <h1 class="v2-headline v2-headline--display-lg v2-affiliates__title">
          Submiembros
        </h1>
        <p class="v2-body v2-body--md v2-body--muted">
          Gestiona la información de los submiembros/colaboradores de tu empresa a quienes son extensivos los beneficios de la organización
        </p>
      </div>
      <div class="v2-affiliates__header-actions">
        <v-tooltip top :z-index="2500" attach=".v2-affiliates" :disabled="!limitEnforced || canAddMore">
          <template #activator="{ on }">
            <span v-on="on">
              <BaseButton
                variant="amber"
                :to="addUrl"
                :disabled="!canAddMore && limitEnforced"
              >
                <template #icon-left>
                  <v-icon size="18">mdi-plus</v-icon>
                </template>
                Agregar submiembro
              </BaseButton>
            </span>
          </template>
          <span>
            Has alcanzado el límite de {{ submemberLimitValue }}
            {{ submemberLimitValue === 1 ? "submiembro" : "submiembros" }}
            permitidos para tu afiliación.
          </span>
        </v-tooltip>
      </div>
    </header>

    <div class="v2-affiliates__toolbar">
      <div class="v2-affiliates__search">
        <v-icon size="18" color="grey darken-1" class="v2-affiliates__search-icon">
          mdi-magnify
        </v-icon>
        <input
          v-model="search"
          type="search"
          class="v2-affiliates__search-input"
          placeholder="Buscar por nombre, apellido o correo…"
          @keypress.enter="onSearch"
        />
      </div>
      <BaseSelect
        v-model="statusFilter"
        :options="statusOptions"
        class="v2-affiliates__status-select"
        @change="onSearch"
      />
      <BaseButton
        variant="secondary"
        :loading="exporting"
        @click="onExport"
      >
        <template #icon-left>
          <v-icon size="18">mdi-download-outline</v-icon>
        </template>
        Exportar CSV
      </BaseButton>
    </div>

    <div v-if="isLoading" class="v2-affiliates__state">
      <v-icon size="32" color="grey lighten-1">mdi-progress-clock</v-icon>
      <p class="v2-body v2-body--sm v2-body--muted">Cargando submiembros…</p>
    </div>

    <div v-else-if="error" class="v2-affiliates__state">
      <EmptyState
        title="No se pudieron cargar los submiembros"
        :description="error"
        icon="mdi-cloud-off-outline"
      >
        <BaseButton variant="primary" @click="loadAffiliates">
          <template #icon-left>
            <v-icon size="18">mdi-refresh</v-icon>
          </template>
          Reintentar
        </BaseButton>
      </EmptyState>
    </div>

    <div
      v-else-if="!affiliates.length"
      class="v2-affiliates__state"
    >
      <EmptyState
        title="Aún no tienes submiembros"
        description="Agrega colaboradores o familiares vinculados a tu membresía para que puedan acceder a los beneficios de tu cámara."
        icon="mdi-account-multiple-plus-outline"
      >
        <v-tooltip top :z-index="2500" attach=".v2-affiliates" :disabled="!limitEnforced || canAddMore">
          <template #activator="{ on }">
            <span v-on="on">
              <BaseButton
                variant="amber"
                :to="addUrl"
                :disabled="!canAddMore && limitEnforced"
              >
                <template #icon-left>
                  <v-icon size="18">mdi-plus</v-icon>
                </template>
                Agregar el primero
              </BaseButton>
            </span>
          </template>
          <span>
            Has alcanzado el límite de {{ submemberLimitValue }}
            {{ submemberLimitValue === 1 ? "submiembro" : "submiembros" }}
            permitidos para tu afiliación.
          </span>
        </v-tooltip>
      </EmptyState>
    </div>

    <div
      v-else-if="!filteredAffiliates.length"
      class="v2-affiliates__state"
    >
      <EmptyState
        title="Sin resultados"
        description="No encontramos submiembros que coincidan con tu búsqueda. Ajusta los filtros e intenta nuevamente."
        icon="mdi-account-search-outline"
      >
        <BaseButton variant="ghost" @click="clearFilters">
          Limpiar filtros
        </BaseButton>
      </EmptyState>
    </div>

    <div v-else class="v2-affiliates__table-wrapper">
      <table class="v2-affiliates__table">
        <thead>
          <tr>
            <th class="v2-affiliates__th v2-affiliates__th--name">Submiembro</th>
            <th class="v2-affiliates__th">Fecha de Creación</th>
            <th class="v2-affiliates__th">Estado</th>
            <th class="v2-affiliates__th v2-affiliates__th--actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="affiliate in filteredAffiliates">
            <AffiliateRow
              :key="getRowKey(affiliate)"
              :affiliate="affiliate"
              :expanded="isExpanded(affiliate)"
              @toggle="onRowToggle"
              @edit="onEdit"
              @delete="onDelete"
              @resend="onResend"
            />
            <AffiliateDetailRow
              v-if="isExpanded(affiliate)"
              :key="`${getRowKey(affiliate)}-detail`"
              :affiliate="affiliate"
              :colspan="4"
            />
          </template>
        </tbody>
      </table>

      <div class="v2-affiliates__pagination">
        <span class="v2-affiliates__pagination-info">
          Mostrando {{ filteredAffiliates.length }} de {{ totalDocs }}
          {{ totalDocs === 1 ? "submiembro" : "submiembros" }}
        </span>
        <div class="v2-affiliates__pagination-controls">
          <button
            type="button"
            class="v2-affiliates__page-btn"
            :disabled="page <= 1"
            @click="onPageChange(page - 1)"
            aria-label="Página anterior"
          >
            <v-icon size="18">mdi-chevron-left</v-icon>
          </button>
          <span class="v2-affiliates__page-info">
            Página {{ page }} de {{ totalPages || 1 }}
          </span>
          <button
            type="button"
            class="v2-affiliates__page-btn"
            :disabled="page >= totalPages"
            @click="onPageChange(page + 1)"
            aria-label="Página siguiente"
          >
            <v-icon size="18">mdi-chevron-right</v-icon>
          </button>
        </div>
      </div>
    </div>

    <BaseModal
      v-if="deleteDialogOpen"
      :open="deleteDialogOpen"
      title="Eliminar submiembro"
      @close="closeDeleteDialog"
    >
      <p class="v2-body v2-body--md">
        ¿Seguro que quieres eliminar a
        <strong>{{ affiliateToDelete ? getAffiliateFullName(affiliateToDelete) : "" }}</strong>?
        Esta acción no se puede deshacer.
      </p>
      <template #footer>
        <BaseButton variant="secondary" @click="closeDeleteDialog">
          Cancelar
        </BaseButton>
        <BaseButton
          variant="danger"
          :loading="deleting"
          @click="confirmDelete"
        >
          <template #icon-left>
            <v-icon size="18">mdi-trash-can-outline</v-icon>
          </template>
          Eliminar
        </BaseButton>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import BaseButton from "@/components/memberDashboardV2/ui/BaseButton.vue";
import BaseModal from "@/components/memberDashboardV2/ui/BaseModal.vue";
import BaseSelect from "@/components/memberDashboardV2/ui/BaseSelect.vue";
import EmptyState from "@/components/memberDashboardV2/ui/EmptyState.vue";
import StatCard from "@/components/memberDashboardV2/ui/StatCard.vue";
import AffiliateRow from "./components/AffiliateRow.vue";
import AffiliateDetailRow from "./components/AffiliateDetailRow.vue";
import {
  getAffiliateFullName,
} from "./affiliatesHelpers";
import { resolveMemberAffiliateBranch } from "@/common/memberDashboardRouting";

const PAGE_SIZE = 10;

export default {
  name: "MemberDashboardV2Affiliates",
  components: {
    BaseButton,
    BaseModal,
    BaseSelect,
    EmptyState,
    StatCard,
    AffiliateRow,
    AffiliateDetailRow,
  },
  data() {
    return {
      isLoading: false,
      error: null,
      affiliates: [],
      search: "",
      statusFilter: "",
      page: 1,
      totalPages: 0,
      totalDocs: 0,
      totalSubmembers: 0,
      deleteDialogOpen: false,
      affiliateToDelete: null,
      deleting: false,
      exporting: false,
      expandedAffiliateId: null,
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    ...mapState("membershipProfile", ["userMembership"]),
    statusOptions() {
      return [
        { value: "", label: "Todos los estados" },
        { value: "ACTIVE", label: "Activo" },
        { value: "PROSPECTO", label: "Pendiente" },
        { value: "INACTIVO", label: "Inactivo" },
        { value: "EXPIRED", label: "Expirado" },
      ];
    },
    addUrl() {
      return "/member-v2/affiliates/new";
    },
    submemberLimit() {
      const details = this.user?.affiliation?.national?.details;
      if (!Array.isArray(details) || !details.length) return null;
      const sublimit = details[0]?.affiliationType?.submembersLimit;
      if (!sublimit) return null;
      return sublimit;
    },
    limitEnforced() {
      return Boolean(this.submemberLimit && this.submemberLimit.active);
    },
    submemberLimitValue() {
      return this.submemberLimit?.value || 0;
    },
    canAddMore() {
      if (!this.limitEnforced) return true;
      // El límite cuenta TODOS los submiembros creados (cualquier estado y
      // cualquier branch, excepto eliminados), igual que el backend al crear.
      return this.totalSubmembers < this.submemberLimitValue;
    },
    filteredAffiliates() {
      const term = (this.search || "").trim().toLowerCase();
      if (!term) return this.affiliates;
      return this.affiliates.filter((a) => {
        const name = (a.name || "").toLowerCase();
        const last = (a.firstLastname || "").toLowerCase();
        const email = (a.email || "").toLowerCase();
        return (
          name.includes(term) || last.includes(term) || email.includes(term)
        );
      });
    },
    totalCount() {
      return this.totalDocs;
    },
    activeCount() {
      return this.affiliates.filter((a) => a.validationStatus === "ACTIVE").length;
    },
    pendingCount() {
      return this.affiliates.filter((a) => a.validationStatus === "PROSPECTO").length;
    },
    inactiveCount() {
      return this.affiliates.filter(
        (a) =>
          a.validationStatus === "INACTIVO" ||
          a.validationStatus === "EXPIRED" ||
          a.validationStatus === "DELETED"
      ).length;
    },
  },
  methods: {
    ...mapActions("memberAffiliates", [
      "getAffiliates",
      "deleteAffiliate",
      "exportAffiliates",
    ]),
    ...mapActions("user", ["realoadUserData"]),
    ...mapMutations("loader", { showLoader: "loading", hideLoader: "loaded" }),
    ...mapMutations("notification", ["show"]),
    getAffiliateFullName,
    getRowKey(affiliate) {
      return affiliate && (affiliate._id || affiliate.id);
    },
    isExpanded(affiliate) {
      return this.expandedAffiliateId === this.getRowKey(affiliate);
    },
    onRowToggle(affiliate) {
      const id = this.getRowKey(affiliate);
      this.expandedAffiliateId = this.expandedAffiliateId === id ? null : id;
    },
    buildQuery() {
      return {
        page: this.page,
        limit: PAGE_SIZE,
        name: (this.search || "").trim(),
        validationStatus: this.statusFilter || "",
        branch: resolveMemberAffiliateBranch(this.user, this.userMembership) || "",
      };
    },
    async loadAffiliates() {
      this.isLoading = true;
      this.error = null;
      try {
        this.showLoader();
        const response = await this.getAffiliates(this.buildQuery());
        if (response && response.status && response.affiliates) {
          const data = response.affiliates.data || {};
          this.affiliates = data.members || [];
          this.totalPages = data.totalPages || 0;
          this.totalDocs = data.totalDocs || 0;
          this.totalSubmembers = data.totalSubmembers ?? this.totalDocs;
        } else {
          this.affiliates = [];
          this.totalPages = 0;
          this.totalDocs = 0;
          this.totalSubmembers = 0;
        }
      } catch (e) {
        this.affiliates = [];
        this.error =
          "Hubo un problema al obtener los submiembros. Verifica tu conexión e intenta nuevamente.";
        this.show({
          text: "No se pudieron cargar los submiembros",
          color: "error",
        });
      } finally {
        this.hideLoader();
        this.isLoading = false;
      }
    },
    onSearch() {
      this.page = 1;
      this.loadAffiliates();
    },
    onPageChange(next) {
      if (next < 1 || next > this.totalPages) return;
      this.page = next;
      this.loadAffiliates();
    },
    clearFilters() {
      this.search = "";
      this.statusFilter = "";
      this.page = 1;
      this.loadAffiliates();
    },
    onEdit(affiliate) {
      this.$router.push({
        path: "/member-v2/affiliates/new",
        query: {
          edit: affiliate._id || affiliate.id,
        },
      });
    },
    onResend() {
      this.show({
        text: "La función de reenviar invitación estará disponible pronto.",
        color: "info",
      });
    },
    onDelete(affiliate) {
      this.affiliateToDelete = affiliate;
      this.deleteDialogOpen = true;
    },
    closeDeleteDialog() {
      this.deleteDialogOpen = false;
      this.affiliateToDelete = null;
    },
    async confirmDelete() {
      if (!this.affiliateToDelete) return;
      this.deleting = true;
      try {
        const result = await this.deleteAffiliate(this.affiliateToDelete);
        if (result && result.status) {
          this.affiliates = this.affiliates.filter(
            (a) => (a._id || a.id) !== (this.affiliateToDelete._id || this.affiliateToDelete.id)
          );
          this.totalDocs = Math.max(0, this.totalDocs - 1);
          this.totalSubmembers = Math.max(0, this.totalSubmembers - 1);
          this.show({
            text: "Submiembro eliminado correctamente",
            color: "success",
          });
        } else {
          this.show({
            text: result && result.message
              ? `No fue posible eliminar el submiembro: ${result.message}`
              : "No fue posible eliminar el submiembro.",
            color: "error",
          });
        }
      } catch (e) {
        this.show({
          text: "Ocurrió un error al eliminar el submiembro.",
          color: "error",
        });
      } finally {
        this.deleting = false;
        this.closeDeleteDialog();
        if (this.user && this.user.email) {
          try {
            await this.realoadUserData({ email: this.user.email });
          } catch (e) {
            // silent
          }
        }
      }
    },
    async onExport() {
      if (this.exporting) return;
      this.exporting = true;
      try {
        this.showLoader();
        const result = await this.exportAffiliates({
          name: (this.search || "").trim(),
          validationStatus: this.statusFilter || "",
          branch: resolveMemberAffiliateBranch(this.user, this.userMembership) || "",
        });
        if (result && result.status) {
          const rows = (result.members || []).map((m) => ({
            Nombre: m.name || "",
            Apellido: m.firstLastname || "",
            "Correo Electrónico": m.email || "",
            "F. Alta": m.createdAt
              ? new Date(m.createdAt).toISOString().slice(0, 10)
              : "",
            Estado: m.validationStatus || "",
            Cargo: m.position || "",
          }));
          this.downloadCsv(rows, "submiembros.csv");
          this.show({
            text: `Se exportaron ${rows.length} submiembros.`,
            color: "success",
          });
        } else {
          this.show({
            text: "No fue posible exportar los datos.",
            color: "error",
          });
        }
      } catch (e) {
        this.show({
          text: "No fue posible exportar los datos.",
          color: "error",
        });
      } finally {
        this.hideLoader();
        this.exporting = false;
      }
    },
    downloadCsv(rows, filename) {
      if (!Array.isArray(rows) || rows.length === 0) {
        const headers = [
          "Nombre",
          "Apellido",
          "Correo Electrónico",
          "F. Alta",
          "Estado",
          "Cargo",
        ];
        rows = [headers.reduce((acc, h) => ((acc[h] = ""), acc), {})];
      }
      const headers = Object.keys(rows[0] || {});
      const escape = (val) => {
        const s = val === null || val === undefined ? "" : String(val);
        if (/[",\n;]/.test(s)) {
          return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
      };
      const csv = [
        headers.join(","),
        ...rows.map((row) => headers.map((h) => escape(row[h])).join(",")),
      ].join("\n");
      const blob = new Blob(["\ufeff" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
  async mounted() {
    await this.loadAffiliates();
  },
};
</script>

<style scoped>
.v2-affiliates {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.v2-affiliates__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.v2-affiliates__title-block {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  min-width: 0;
  flex: 1 1 auto;
}

.v2-affiliates__title {
  margin: 0;
}

.v2-affiliates__header-actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
}

.v2-affiliates__stats {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-4);
}

@media (min-width: 600px) {
  .v2-affiliates__stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1024px) {
  .v2-affiliates__stats {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.v2-affiliates__toolbar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex-wrap: wrap;
}

.v2-affiliates__search {
  position: relative;
  flex: 1 1 280px;
  min-width: 220px;
  display: flex;
  align-items: center;
}

.v2-affiliates__search-icon {
  position: absolute;
  left: var(--space-3);
  pointer-events: none;
}

.v2-affiliates__search-input {
  width: 100%;
  padding: var(--space-2) var(--space-3) var(--space-2) var(--space-8);
  font-family: var(--font-body);
  font-size: var(--fs-body-sm);
  color: var(--color-text);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);
}

.v2-affiliates__search-input:focus {
  outline: none;
  border-color: var(--color-amber);
  box-shadow: 0 0 0 1px var(--color-amber);
}

.v2-affiliates__status-select {
  flex: 0 0 220px;
  min-width: 200px;
}

.v2-affiliates__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-8) var(--space-4);
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  text-align: center;
}

.v2-affiliates__table-wrapper {
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-xs);
}

.v2-affiliates__table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: var(--fs-body-sm);
  color: var(--color-text);
}

.v2-affiliates__th {
  text-align: left;
  font-weight: var(--fw-semibold);
  color: var(--color-text-muted);
  background-color: var(--color-neutral-50);
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid var(--color-border);
  font-size: var(--fs-label-sm);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.v2-affiliates__th--name {
  min-width: 240px;
}

.v2-affiliates__th--actions {
  text-align: right;
  width: 1%;
}

.v2-affiliates__pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-3) var(--space-4);
  background-color: var(--color-neutral-50);
  border-top: 1px solid var(--color-border);
  gap: var(--space-3);
  flex-wrap: wrap;
}

.v2-affiliates__pagination-info {
  color: var(--color-text-muted);
  font-size: var(--fs-body-sm);
}

.v2-affiliates__pagination-controls {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

.v2-affiliates__page-info {
  font-size: var(--fs-label-sm);
  color: var(--color-text-muted);
  padding: 0 var(--space-2);
  min-width: 90px;
  text-align: center;
}

.v2-affiliates__page-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  cursor: pointer;
  transition: background-color var(--transition-fast), color var(--transition-fast);
}

.v2-affiliates__page-btn:hover:not(:disabled) {
  background-color: var(--color-primary-soft);
  color: var(--color-primary);
}

.v2-affiliates__page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.v2-affiliates__page-btn:focus-visible {
  outline: 2px solid var(--color-amber);
  outline-offset: 2px;
}
</style>
